import { useEffect, useState } from 'react'
import {
  Building2,
  Check,
  Cloud,
  Loader2,
  Lock,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import useClientBook from '@/stores/useClientBook'
import useStore from '@/stores/useStore'
import { supabaseEnv } from '@/lib/supabaseClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { planDisplayName } from '@/lib/planDisplayNames'
import { flushPlanAutosave } from '@/lib/planAutosave'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

// A Client Book action failed. The underlying error can be a raw
// Postgres / RLS / auth / network message – never surface that to the user.
// Log the technical detail for debugging and return a calm, recoverable
// message instead.
const describeClientBookFailure = (error) => {
  console.error('Client Book action failed:', error)
  return 'Please refresh the page and try again. If the problem continues, contact support.'
}

const planSummary = (settings = {}) =>
  ['planA', 'planB', 'planC', 'planD']
    .filter((key) => settings?.[key])
    .map((key) => planDisplayName(key, settings[key].name || key))
    .join(' · ') || 'Current Plan'

// The single, prominent privacy reassurance. One element reused on every Client
// Book screen so the message stays unmistakable without being repeated in a
// dozen different sentences.
function CensusLocalNote() {
  return (
    <p className="flex items-start gap-1.5 border border-border bg-muted/40 px-3 py-2 text-[11px] leading-snug text-foreground">
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
      <span>
        Your employee data never leaves this browser. Only the client label and plan settings
        sync to your account.
      </span>
    </p>
  )
}

function SectionHeading({ children, action = null }) {
  return (
    <div className="flex items-center justify-between">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {children}
      </h4>
      {action}
    </div>
  )
}

// A labeled action in a client row's "⋯" menu. Icons stay (they read nicely) but
// every action is spelled out so nothing relies on guessing what a glyph means.
function RowMenuItem({ icon: Icon, children, onClick, destructive = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-muted',
        destructive ? 'text-destructive hover:bg-destructive/10' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {children}
    </button>
  )
}

// Compact status indicator retained for surfaces that need to show which saved
// client, if any, supplies the current plan settings.
export default function ClientBookChip({ locked = false, onOpen }) {
  const enabled = useClientBook((state) => state.enabled)
  const clients = useClientBook((state) => state.clients)
  const activeClientId = useClientBook((state) => state.activeClientId)
  const refresh = useClientBook((state) => state.refresh)

  // The opt-in flag is persisted, but the client list is fetched per session.
  // The chip is always mounted, so it owns the one-time refresh when the book is
  // on. Skipped while locked (signed-out preview) since data is account-scoped.
  useEffect(() => {
    if (!locked && enabled && supabaseEnv.isReady) refresh().catch(() => {})
  }, [locked, enabled, refresh])

  // Cloud isn't configured for this deployment – hide the feature entirely.
  if (!supabaseEnv.isReady) return null

  const activeClient = clients.find((client) => client.id === activeClientId) || null
  const label =
    locked || !enabled
      ? 'Client plans'
      : activeClient
      ? activeClient.client_name
      : 'Unsaved plan settings'

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-6 max-w-[180px] px-2 text-[11px]"
      onClick={onOpen}
      title="Open client plans"
    >
      <Building2 className="mr-1 h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </Button>
  )
}

// The manager body has one job per mode: name/duplicate the current plan,
// switch clients, or manage names and deletion. Active plans save in the
// background, so no manual update/overwrite actions appear here.
export function ClientBookBody({ locked = false, loginHref = null, intent = 'manage', onComplete = null }) {
  const enabled = useClientBook((state) => state.enabled)
  const clients = useClientBook((state) => state.clients)
  const activeClientId = useClientBook((state) => state.activeClientId)
  const loading = useClientBook((state) => state.loading)
  const saving = useClientBook((state) => state.saving)
  const refresh = useClientBook((state) => state.refresh)
  const enableCloud = useClientBook((state) => state.enableCloud)
  const selectClient = useClientBook((state) => state.selectClient)
  const saveAsNew = useClientBook((state) => state.saveAsNew)
  const renameClient = useClientBook((state) => state.renameClient)
  const removeClient = useClientBook((state) => state.removeClient)
  const employeeCount = useStore((state) => state.employees.length)

  // Fetch the client list whenever the manager opens (Radix mounts this body on
  // open). Keeps "Your clients" current without a manual refresh; skipped while
  // locked (signed-out preview) since the data is account-scoped.
  useEffect(() => {
    if (!locked && enabled && supabaseEnv.isReady) refresh().catch(() => {})
  }, [locked, enabled, refresh])

  const [enabling, setEnabling] = useState(false)
  const [showSaveForm, setShowSaveForm] = useState(intent === 'save')
  const [newName, setNewName] = useState('')
  const [renamingId, setRenamingId] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const [menuOpenId, setMenuOpenId] = useState(null)
  // Promise-based confirm so the async flows can `await confirm(...)`. Holds the
  // prompt copy plus the resolver that settles when the user picks an option.
  const [confirmState, setConfirmState] = useState(null)

  useEffect(() => {
    if (intent === 'save') setShowSaveForm(true)
  }, [intent])

  // Signed-out preview (e.g. the public demo): visible for discovery but gated.
  if (locked) {
    return (
      <div className="space-y-3">
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <Lock className="h-4 w-4 text-muted-foreground" />
          Log in to use saved clients
        </p>
        <p className="text-xs leading-5 text-muted-foreground">
          Save and sync client plan settings across sessions and devices. Available to signed-in
          advisors.
        </p>
        <CensusLocalNote />
        {loginHref && (
          <Button asChild size="sm">
            <a href={loginHref}>Log in</a>
          </Button>
        )}
      </div>
    )
  }

  // The consent gate. Turning the book on starts syncing client labels and plan
  // settings to the account, so the reassurance banner leads.
  if (!enabled) {
    const handleEnable = async () => {
      setEnabling(true)
      try {
        await enableCloud()
        toast.success('Account saving turned on')
      } catch (error) {
        toast.error('Could not turn on account saving', { description: describeClientBookFailure(error) })
      } finally {
        setEnabling(false)
      }
    }

    return (
      <div className="space-y-3">
        <CensusLocalNote />
        <p className="text-xs leading-5 text-muted-foreground">
          Turn on account saving to save client labels and plan settings, so you
          can pick a client back up across sessions and devices.
        </p>
        <Button size="sm" onClick={handleEnable} disabled={enabling}>
          {enabling ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Cloud className="mr-1.5 h-3.5 w-3.5" />
          )}
          Turn on account saving
        </Button>
      </div>
    )
  }

  const activeClient = clients.find((client) => client.id === activeClientId) || null

  const confirm = (options) =>
    new Promise((resolve) => {
      setConfirmState({ ...options, resolve })
    })

  // Settle the open confirm with the chosen value and clear it. The functional
  // update guarantees we only resolve once even though Radix fires both the
  // button onClick and the subsequent onOpenChange(false).
  const resolveConfirm = (value) => {
    setConfirmState((current) => {
      current?.resolve(value)
      return null
    })
  }

  const withToast = (label) => async (promise) => {
    try {
      await promise
    } catch (error) {
      toast.error(`Could not ${label}`, { description: describeClientBookFailure(error) })
    }
  }

  const handleSelect = async (id) => {
    if (id === activeClientId) {
      onComplete?.()
      return
    }
    if (id !== activeClientId && employeeCount > 0) {
      const ok = await confirm({
        title: 'Load these plan settings?',
        description:
          'This applies the client’s saved plan settings to the current employee data. Employee data is not replaced. Compare turns off.',
        confirmLabel: 'Load plan settings',
      })
      if (!ok) return
    }
    try {
      await flushPlanAutosave()
      await selectClient(id)
      toast.success('Loaded client plan settings')
      onComplete?.()
    } catch (error) {
      toast.error('Could not load client', { description: describeClientBookFailure(error) })
    }
  }

  const handleDelete = async (client) => {
    const ok = await confirm({
      title: `Delete “${client.client_name}”?`,
      description:
        'Remove this saved client. This deletes the client label and plan settings only. Employee data is untouched.',
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!ok) return
    await withToast('remove client')(
      removeClient(client.id).then(() => toast.success('Client removed')),
    )
  }

  const handleSaveNew = async (event) => {
    event.preventDefault()
    const trimmed = newName.trim()
    if (!trimmed) return
    await withToast('save client')(
      saveAsNew(trimmed).then(() => {
        toast.success(`Saved “${trimmed}”`)
        setNewName('')
        setShowSaveForm(false)
        onComplete?.()
      }),
    )
  }

  const startRename = (client) => {
    setRenamingId(client.id)
    setRenameValue(client.client_name)
  }

  const runRowAction = (action) => {
    setMenuOpenId(null)
    action()
  }

  const handleRename = async (event) => {
    event.preventDefault()
    const id = renamingId
    const trimmed = renameValue.trim()
    setRenamingId(null)
    const current = clients.find((client) => client.id === id)
    if (!id || !trimmed || trimmed === current?.client_name) return
    await withToast('rename client')(
      renameClient(id, trimmed).then(() => toast.success('Client renamed')),
    )
  }

  const toggleSaveForm = () => {
    setNewName('')
    setShowSaveForm((open) => !open)
  }

  return (
    <div className="space-y-5">
      <CensusLocalNote />

      {activeClient && intent !== 'save' && (
        <div className="flex flex-wrap items-center gap-2 border border-primary/30 bg-primary/5 px-3 py-2.5">
          <span className="flex min-w-0 items-center gap-1.5 text-sm">
            <Building2 className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-muted-foreground">Open:</span>
            <span className="truncate font-medium text-foreground">{activeClient.client_name}</span>
          </span>
          <span className="ml-auto text-[11px] text-muted-foreground">Changes save automatically</span>
        </div>
      )}

      {intent === 'save' && <section className="space-y-2">
        <SectionHeading>{activeClient ? 'Duplicate client' : 'Name client'}</SectionHeading>
        {showSaveForm ? (
          <form onSubmit={handleSaveNew} className="flex items-center gap-2">
            <Input
              autoFocus
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="e.g. Acme Manufacturing"
              aria-label="New client name"
              className="h-8"
            />
            <Button type="submit" size="sm" disabled={saving || !newName.trim()}>
              {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              {activeClient ? 'Duplicate' : 'Save name'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={toggleSaveForm}>
              Cancel
            </Button>
          </form>
        ) : (
          <Button variant="outline" size="sm" onClick={toggleSaveForm}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {activeClient ? 'Duplicate this client' : 'Name this client'}
          </Button>
        )}
      </section>}

      {intent !== 'save' && <section className="space-y-2">
        <SectionHeading
          action={
            <button
              type="button"
              onClick={() => withToast('refresh')(refresh())}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Refresh"
              aria-label="Refresh client list"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </button>
          }
        >
          Saved clients
        </SectionHeading>

        {clients.length === 0 ? (
          <p className="border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            No saved clients yet. Save the current plan settings above to start.
          </p>
        ) : (
          <div className="max-h-72 divide-y divide-border overflow-auto border border-border">
              {clients.map((client) => {
                const settings = client.plan_settings || {}
                const isActive = client.id === activeClientId
                const isRenaming = renamingId === client.id
                const canLoad = intent === 'manage'
                return (
                  <div
                    key={client.id}
                    role={!isRenaming && canLoad ? 'button' : undefined}
                    tabIndex={!isRenaming && canLoad ? 0 : undefined}
                    onClick={!isRenaming && canLoad ? () => handleSelect(client.id) : undefined}
                    onKeyDown={
                      isRenaming || !canLoad
                        ? undefined
                        : (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              handleSelect(client.id)
                            }
                          }
                    }
                    className={[
                      'flex items-center gap-3 px-3 py-2.5',
                      !isRenaming && canLoad ? 'cursor-pointer hover:bg-muted/60' : '',
                      isActive ? 'bg-primary/5' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Check
                      className={`h-4 w-4 shrink-0 ${isActive ? 'text-primary' : 'text-transparent'}`}
                    />
                    <div className="min-w-0 flex-1">
                      {isRenaming ? (
                        <form onSubmit={handleRename} onClick={(event) => event.stopPropagation()}>
                          <Input
                            autoFocus
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            onBlur={() => setRenamingId(null)}
                            className="h-7"
                            aria-label="Client name"
                          />
                        </form>
                      ) : (
                        <>
                          <p className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                            <span className="truncate">{client.client_name}</span>
                            {settings.estimate?.inputs && (
                              <span
                                className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
                                title="Saved from Estimate Mode. Loading rebuilds the sample employee list from the saved assumptions."
                              >
                                Estimate
                              </span>
                            )}
                          </p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            {planSummary(settings)}
                          </p>
                        </>
                      )}
                    </div>
                    {!isRenaming && (
                      <Popover
                        open={menuOpenId === client.id}
                        onOpenChange={(open) => setMenuOpenId(open ? client.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            onClick={(event) => event.stopPropagation()}
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                            title="Actions"
                            aria-label={`Actions for ${client.client_name}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="end"
                          sideOffset={4}
                          className="w-56 gap-0.5 p-1"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <RowMenuItem icon={Pencil} onClick={() => runRowAction(() => startRename(client))}>
                            Rename
                          </RowMenuItem>
                          <RowMenuItem icon={Trash2} destructive onClick={() => runRowAction(() => handleDelete(client))}>
                            Delete
                          </RowMenuItem>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                )
              })}
          </div>
        )}
      </section>}

      <AlertDialog
        open={Boolean(confirmState)}
        onOpenChange={(open) => {
          if (!open) resolveConfirm(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmState?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => resolveConfirm(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmState?.destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
              onClick={() => resolveConfirm(true)}
            >
              {confirmState?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
